/**
 * PebblesProgressCard component
 * CSS-only 7-day bar chart showing daily pebbles progress
 * No external chart libraries - uses Tailwind and inline styles
 */

import { useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { DailyPebbles } from '@/types/dashboard';

export interface PebblesProgressCardProps {
  /** Daily pebbles data (up to 14 entries from backend; component uses first 7, sorted newest first) */
  dailyPebbles: DailyPebbles[];
}

/**
 * Pads the dailyPebbles array to exactly 7 entries
 * If fewer than 7 entries, fills backward dates with zero pebbles
 * @param data - Original dailyPebbles array (may be empty or partial)
 * @returns Array of exactly 7 DailyPebbles entries
 */
function padTo7Days(data: DailyPebbles[]): DailyPebbles[] {
  // Handle null/undefined
  const safeData = data || [];

  // If we already have 7 or more, take the first 7 (newest first)
  if (safeData.length >= 7) {
    return safeData.slice(0, 7);
  }

  // Need to pad with zeros
  const result: DailyPebbles[] = [...safeData];
  const entriesToAdd = 7 - safeData.length;

  // Find the earliest date in the array, or use today if empty
  let baseDate: Date;
  if (safeData.length > 0) {
    // Array is sorted newest first, so last entry is oldest
    const oldestEntry = safeData[safeData.length - 1];
    baseDate = new Date(oldestEntry.date + 'T00:00:00');

    // Check if date is valid, fallback to today if not
    if (isNaN(baseDate.getTime())) {
      baseDate = new Date();
    }
  } else {
    baseDate = new Date();
  }

  // Add missing dates going backward from base date
  for (let i = 1; i <= entriesToAdd; i++) {
    const newDate = new Date(baseDate);
    newDate.setDate(baseDate.getDate() - i);
    const dateString = newDate.toISOString().split('T')[0]; // YYYY-MM-DD
    result.push({ date: dateString, pebbles: 0 });
  }

  return result;
}

/**
 * Formats date string (YYYY-MM-DD) to abbreviated day name
 * @param dateString - Date in YYYY-MM-DD format
 * @returns Abbreviated day name (Mon, Tue, etc.) or M/D fallback
 */
function formatDateLabel(dateString: string): string {
  try {
    // Append T00:00:00 to parse as local time (not UTC)
    const date = new Date(dateString + 'T00:00:00');

    // Check if valid date
    if (isNaN(date.getTime())) {
      // Fallback: extract M/D from YYYY-MM-DD
      const parts = dateString.split('-');
      if (parts.length === 3) {
        return `${parseInt(parts[1], 10)}/${parseInt(parts[2], 10)}`;
      }
      return dateString;
    }

    return date.toLocaleDateString('en-US', { weekday: 'short' });
  } catch {
    // Fallback on error
    return dateString;
  }
}

/**
 * PebblesProgressCard - displays a 7-day CSS-only bar chart
 */
export function PebblesProgressCard({ dailyPebbles }: PebblesProgressCardProps) {
  // Pad to exactly 7 entries and reverse so oldest is on left
  const chartData = useMemo(() => {
    const padded = padTo7Days(dailyPebbles);
    // Reverse: newest first -> oldest first (left to right time progression)
    return padded.reverse();
  }, [dailyPebbles]);

  // Find max pebbles for height scaling
  const maxPebbles = useMemo(() => {
    const max = Math.max(...chartData.map((d) => d.pebbles));
    // Avoid division by zero: if all are 0, use 1 (bars will still be 0% height)
    return max === 0 ? 1 : max;
  }, [chartData]);

  return (
    <Card className="mb-4 md:mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base md:text-lg font-semibold text-otter-earth-800">
          Pebbles Progress
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className="flex gap-1 h-32 md:h-40"
          aria-label="Pebbles progress chart showing daily pebbles earned over the last 7 days"
        >
          {chartData.map((entry, index) => {
            // Calculate height percentage
            const heightPercent = Math.round((entry.pebbles / maxPebbles) * 100);

            return (
              <div
                key={`${entry.date}-${index}`}
                className="flex-1 flex flex-col items-center h-full"
              >
                {/* Count label - only shown for non-zero pebble days */}
                {entry.pebbles > 0 && (
                  <span
                    data-testid="pebbles-count-label"
                    className="text-[10px] md:text-xs text-gray-600 font-semibold"
                  >
                    {entry.pebbles}
                  </span>
                )}
                {/* Bar wrapper - takes remaining vertical space */}
                <div className="flex-1 w-full flex items-end justify-center">
                  <div
                    data-testid="pebbles-bar"
                    className={cn(
                      'w-full rounded-t-sm bg-pebbles-500',
                      entry.pebbles > 0 && 'min-h-[2px]'
                    )}
                    style={{ height: `${heightPercent}%` }}
                    aria-label={`${entry.pebbles} pebbles on ${formatDateLabel(entry.date)}`}
                  />
                </div>
                {/* Date label */}
                <span className="text-[10px] md:text-xs text-gray-500 truncate w-full text-center">
                  {formatDateLabel(entry.date)}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
